import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, X } from "@phosphor-icons/react";
import api, { apiErr } from "@/lib/api";
import { Btn } from "@/components/common";

// ONE WAY TO ADD A NON-529 CATEGORY, USED BY EVERY SCREEN THAT OFFERS IT. (ZEN-512)
//
// Owner instruction, 2 September 2026: "point to ONE way of doing the same
// thing where possible, so that if there is ever an issue, one place needs to
// be fixed, not 4 or 5 places like we are facing now." ZEN-56 built this box
// inside the expense form and three other screens kept sending people to
// Settings in a second tab, where the half-done review was left behind and the
// new category was never chosen. This file is that box, moved out so every
// screen uses the same one: the option's words, the box, the save, and what
// happens to the list afterwards. A screen only decides where the box appears
// and what to do with the name the server kept.
//
// A panel, not a page and not a second browser tab. The screen around it stays
// mounted the entire time, which is the whole fix: nothing is taken down, so
// nothing has to be saved and restored. On a batch screen every other row stays
// exactly as it was. Cancel closes the box and changes nothing.
//
// The category is saved for real — it goes onto the beneficiary's own list, the
// same list Settings writes to, so it is there again next month. (Owner ruling,
// 2026-08-09.)

// The hidden value behind the add entry in a picker. Never stored on anything —
// it only tells the screen which choice was made.
export const ADD_CATEGORY_OPTION = "__add_category__";
// Owner-approved wording (ZEN-56). Not "…in Settings": it does not go there.
export const ADD_CATEGORY_LABEL = "＋ Add a category";
// What a screen says when the beneficiary has no non-529 category yet.
// Approved by Maryse on ZEN-512, 16 October 2026 (rule 13). Held here, in one
// place, so a change of words is one edit.
export const NO_FAMILY_CATEGORIES_HINT = "No non-529 categories yet — choose ＋ Add a category.";

// The server is the authority — it trims every non-529 category to 40
// characters (backend/routes/students.py) and Settings uses the same number.
// Repeated here so the box stops where the save stops, rather than accepting a
// longer name and quietly storing a shorter one.
export const NONELIGIBLE_CATEGORY_MAX_LENGTH = 40;

// THE ONE SAVE. Settings and the box both add a category through this.
//
// ONE FIELD, THROUGH A DOOR THAT CHANGES ONE FIELD. Sending the whole
// beneficiary back (PUT /students) would overwrite a school or a
// cost-of-attendance figure changed in another tab, and refuses a beneficiary
// whose school date came through the old migration as a month. (ZEN-56)
export async function saveNonEligibleCategory(studentId, name) {
  const { data } = await api.post(`/students/${studentId}/noneligible-categories`, { name });
  return { stored: data?.matched_category || null, list: data?.noneligible_categories || [] };
}

// THE BROWSER'S COPY OF THE LIST, updated from the save's own reply, so a
// picker can offer the new category the moment it is chosen. ONLY THAT LIST:
// dropping the whole beneficiary in from the reply would strip fields the list
// screens serve and a write does not (a foreign-school flag, the sign-in
// address). Then both copies are refreshed from the server. (ZEN-56)
export function rememberCategoryList(qc, studentId, list) {
  qc.setQueryData(["students"], (old) => Array.isArray(old)
    ? old.map((s) => (s.id === studentId ? { ...s, noneligible_categories: list } : s))
    : old);
  qc.setQueryData(["student", studentId], (old) => (old && typeof old === "object"
    ? { ...old, noneligible_categories: list } : old));
  qc.invalidateQueries({ queryKey: ["students"] });
  qc.invalidateQueries({ queryKey: ["student", studentId] });
}

/**
 * The box. `student` is whose list the category goes on. `onChosen(name)` is
 * called with the name THE SERVER KEPT (it trims and shortens), and only while
 * the box is still open for that same beneficiary and `isStillWanted(studentId)`
 * (optional) still says yes — a reply that arrives after the person cancelled,
 * changed the beneficiary or changed the 529 answer updates the list but
 * chooses nothing.
 */
export function AddCategoryInline({ student, onChosen, onCancel, isStillWanted, testid = "exp-cat-add" }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const typed = name.trim();
  const existing = student?.noneligible_categories || [];
  // Whether the box is still on screen, and for whom, when the reply arrives.
  const open = useRef(true);
  const forStudent = useRef(student?.id);
  useEffect(() => { forStudent.current = student?.id; }, [student?.id]);
  // Set on every mount, not only at creation: React's development mode mounts,
  // unmounts and mounts again, and a box left thinking it was closed would save
  // and then choose nothing. (Review round 1.)
  useEffect(() => { open.current = true; return () => { open.current = false; }; }, []);

  // NO CASE-FOLDING, deliberately. Whether two spellings are one category is
  // the server's rule, and JavaScript disagrees with it beyond English (Greek
  // "ΟΣ" and "οσ"). The screen only compares EXACTLY against lists it already
  // holds, and asks the server which name it kept. (ZEN-56, review round 10.)
  const save = async () => {
    if (!typed || !student) return;
    const savedFor = student.id;
    setBusy(true);
    try {
      const { stored, list } = await saveNonEligibleCategory(savedFor, typed);
      if (!stored) { toast.error("The category was not saved — try again"); return; }
      toast.success(existing.includes(stored)
        ? `"${stored}" is already on the list`
        : `"${stored}" added for ${student.nickname || student.full_name}`);
      rememberCategoryList(qc, savedFor, list);
      // THE CATEGORY BELONGS TO THE BENEFICIARY IT WAS ADDED FOR. A reply from
      // a box that was closed, or for a beneficiary no longer chosen, is
      // finished business: the list is updated, nothing is chosen. (ZEN-56,
      // review round 4.)
      const stillHere = open.current && forStudent.current === savedFor
        && (!isStillWanted || isStillWanted(savedFor));
      if (stillHere) onChosen(stored);
    } catch (e) { toast.error(apiErr(e)); }
    finally { if (open.current) setBusy(false); }
  };

  return (
    <div data-enter-scope className="mt-2 rounded-lg border border-dashed border-[#034AA2]/40 bg-[#034AA2]/5 p-3" data-testid={`${testid}-form`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-[#034AA2]">New category</span>
        <button type="button" onClick={onCancel} className="shrink-0 text-[#6b7280] hover:text-[#1f2937]" aria-label="cancel adding a category" data-testid={`${testid}-x`}><X size={16} weight="bold" /></button>
      </div>
      <input
        type="text"
        value={name}
        autoFocus
        onChange={(e) => setName(e.target.value.slice(0, NONELIGIBLE_CATEGORY_MAX_LENGTH))}
        // Enter SAVES here rather than moving on — this box is the whole panel,
        // and the app-wide next-field rule steps aside for a handled keypress.
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (typed && !busy) save(); } }}
        maxLength={NONELIGIBLE_CATEGORY_MAX_LENGTH}
        data-testid={`${testid}-input`}
        placeholder="e.g., Wrestling, Subway pass"
        className="h-9 w-full rounded-md border border-[#C2C9D1] bg-white px-2 text-sm focus:border-[#034AA2] focus:outline-none focus:ring-2 focus:ring-[#034AA2]/30" />
      <p className="mt-1 text-[10px] leading-tight text-[#6b7280]">
        Kept for {student?.nickname || student?.full_name || "this beneficiary"} and available every time. {typed.length}/{NONELIGIBLE_CATEGORY_MAX_LENGTH} characters.
      </p>
      <div className="mt-2 flex flex-wrap justify-end gap-2">
        <Btn variant="ghost" className="h-8" onClick={onCancel} data-testid={`${testid}-cancel`}>Cancel</Btn>
        <Btn enterPrimary variant="green" className="h-8" onClick={save} disabled={busy || !typed || !student} data-testid={`${testid}-save`}>
          <Plus size={14} weight="bold" /> {busy ? "Saving…" : "Save category"}
        </Btn>
      </div>
    </div>
  );
}
